import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
import { DeleteConfirmation } from '../ui/DeleteConfirmation';
import {
  UserGroupIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  StarIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentPlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  deleteDoc,
  addDoc,
  getDocs,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { useAuth } from '../../context/AuthContext';

const accent = 'blue';
const accentText = 'text-black';
const accentBg = 'bg-blue-50';
const accentBorder = 'border-black/20';
const accentButton = 'bg-blue-600 hover:bg-blue-700';
const accentButtonText = 'text-white';
const accentButtonOutline = 'border-black/20 hover:bg-black/5';
const accentProgress = 'bg-blue-600';
const accentProgressBg = 'bg-blue-50';
const accentCard = 'bg-white';
const accentCardBorder = 'border-black/20';
const accentMuted = 'text-black/60';
const accentMutedBg = 'bg-gray-50';
const accentDanger = 'bg-red-50 text-red-600';
const accentSuccess = 'bg-emerald-50 text-emerald-600';
const accentWarning = 'bg-amber-50 text-amber-600';

// Live Table Component with Filters and Search
const LiveTable = ({
  title,
  data,
  columns,
  filters,
  searchTerm,
  onSearchChange,
  onFilterChange,
  onExport,
  onView,
  onEdit,
  onDelete,
  loading,
}) => {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredData = data.filter(item => {
    // Enhanced search - search across key fields
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm
      ? item.firstname?.toLowerCase().includes(searchLower) ||
        item.lastname?.toLowerCase().includes(searchLower) ||
        item.email?.toLowerCase().includes(searchLower) ||
        item.organization?.toLowerCase().includes(searchLower) ||
        item.phone?.toLowerCase().includes(searchLower) ||
        `${item.firstname || ''} ${item.lastname || ''}`.toLowerCase().includes(searchLower) ||
        // Fallback to search all values if no specific match
        Object.values(item).some(value => value?.toString().toLowerCase().includes(searchLower))
      : true;

    const matchesFilters = Object.entries(filters).every(
      ([key, value]) => !value || item[key] === value
    );
    return matchesSearch && matchesFilters;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    return sortDirection === 'asc'
      ? aVal.toString().localeCompare(bVal.toString())
      : bVal.toString().localeCompare(aVal.toString());
  });

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading {title}...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-6 shadow-lg"
    >
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-black">{title}</h3>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onExport(sortedData)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </motion.button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        {/* Enhanced Search Bar */}
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-black/40" />
              <input
                type="text"
                placeholder={`Search by name, email, organization...`}
                value={searchTerm}
                onChange={e => onSearchChange(e.target.value)}
                className="w-full rounded-lg border border-black/20 py-2.5 pl-10 pr-10 text-black placeholder-black/40 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
              />
              {searchTerm && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-black/40 transition-colors hover:bg-black/5 hover:text-black/60"
                  aria-label="Clear search"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="text-sm text-black/60">
                {sortedData.length} result{sortedData.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(filters).map(([key, value]) => (
            <select
              key={key}
              value={value}
              onChange={e => onFilterChange(key, e.target.value)}
              className="rounded-lg border border-black/20 px-3 py-2 text-sm text-black focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
            >
              <option value="">All {key}</option>
              {Array.from(new Set(data.map(item => item[key]).filter(Boolean))).map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-black/20">
              {columns.map(column => (
                <th
                  key={column.key}
                  onClick={() => column.sortable && handleSort(column.key)}
                  className={`px-4 py-3 text-left text-sm font-medium text-black ${
                    column.sortable ? 'cursor-pointer hover:bg-black/5' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    {column.sortable && sortField === column.key && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-sm font-medium text-black">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-black/60">
                  No {title.toLowerCase()} found
                </td>
              </tr>
            ) : (
              sortedData.map((item, index) => (
                <motion.tr
                  key={item.id || index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-black/10 hover:bg-black/5"
                >
                  {columns.map(column => (
                    <td key={column.key} className="px-4 py-3 text-sm text-black">
                      {column.render ? column.render(item[column.key], item) : item[column.key]}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {onView && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onView(item)}
                          className="rounded-lg p-2 text-black hover:bg-black/10"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </motion.button>
                      )}
                      {onEdit && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onEdit(item)}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </motion.button>
                      )}
                      {onDelete && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onDelete(item)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </motion.button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-black/60">
        Showing {sortedData.length} of {data.length} {title.toLowerCase()}
      </div>
    </motion.div>
  );
};

const AssignedClientsDashboard = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [clients, setClients] = useState([]);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const clientsPerPage = 2;

  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Get effective email (parent coach email if delegated user)
    const delegatedUserData = localStorage.getItem('delegatedUser');
    let effectiveEmail = currentUser.email;

    if (delegatedUserData) {
      try {
        const parsed = JSON.parse(delegatedUserData);
        effectiveEmail = parsed.coachEmail || currentUser.email;
      } catch (e) {
        // Invalid JSON, use current user email
      }
    }

    // Fetch clients assigned to this coach
    const clientsQuery = query(
      collection(firestore, 'clients'),
      where('assignedCoachEmail', '==', effectiveEmail)
    );

    const unsubscribeClients = onSnapshot(
      clientsQuery,
      snapshot => {
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClients(clientsData);
        setLoading(false);
      },
      error => {
        toast.error('Failed to fetch clients', 'Error');
        setLoading(false);
      }
    );

    // Fetch session notes to calculate stats
    const notesQuery = query(
      collection(firestore, 'sessionNotes'),
      where('coachEmail', '==', effectiveEmail),
      orderBy('date', 'desc')
    );

    const unsubscribeNotes = onSnapshot(
      notesQuery,
      snapshot => {
        const notesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSessionNotes(notesData);
      },
      () => {}
    );

    return () => {
      unsubscribeClients();
      unsubscribeNotes();
    };
  }, [currentUser?.email]);

  // Calculate client stats from session notes
  const getClientStats = clientId => {
    const clientNotes = sessionNotes.filter(note => note.clientId === clientId);
    const totalSessions = clientNotes.length;
    const lastSession =
      clientNotes.length > 0 && clientNotes[0].date
        ? new Date(clientNotes[0].date).toLocaleDateString()
        : 'N/A';
    const ratings = clientNotes.filter(n => n.rating).map(n => n.rating);
    const averageRating =
      ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : 0;

    return {
      totalSessions,
      lastSession,
      rating: parseFloat(averageRating),
      progress: Math.min(100, totalSessions * 10), // Simple progress calculation
    };
  };

  // Filter clients by status and search term
  const filteredClients = clients.filter(client => {
    // Status filter
    if (filterStatus !== 'all' && client.status !== filterStatus) {
      return false;
    }

    // Search filter (organization or email)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const organization = (client.organization || client.businessName || '').toLowerCase();
      const email = (client.email || '').toLowerCase();
      const matchesSearch = organization.includes(searchLower) || email.includes(searchLower);
      if (!matchesSearch) {
        return false;
      }
    }

    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);
  const startIndex = (currentPage - 1) * clientsPerPage;
  const endIndex = startIndex + clientsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchTerm]);

  const getClientName = client =>
    `${client.firstname || ''} ${client.lastname || ''}`.trim() ||
    client.organization ||
    client.email ||
    'Unknown Client';

  const getInitials = client => {
    const name = getClientName(client);
    return name
      .split(' ')
      .map(part => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const statusStyles = status => {
    switch (status) {
      case 'active':
        return 'bg-emerald-50 text-emerald-600';
      case 'pending':
        return 'bg-amber-50 text-amber-600';
      case 'inactive':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-blue-50 text-blue-600';
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading clients...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-6 shadow-lg"
    >
      <div className="mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserGroupIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-black">Assigned Clients</h2>
            <span className="text-sm text-black/60">({filteredClients.length})</span>
          </div>
          <div className="flex gap-2">
            {['all', 'active', 'pending', 'completed'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-black hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Search Input */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-black/40" />
          <input
            type="text"
            placeholder="Search by organization or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-black/20 py-2.5 pl-10 pr-4 text-sm text-black placeholder-black/40 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-black/5"
            >
              <XMarkIcon className="h-4 w-4 text-black/40" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <h3 className="mb-4 text-lg font-medium text-black">
            Clients ({filteredClients.length})
          </h3>
          <div className="space-y-3">
            {paginatedClients.length === 0 ? (
              <div className="py-8 text-center">
                <UserGroupIcon className="mx-auto mb-4 h-12 w-12 text-blue-400" />
                <p className="text-blue-600">
                  {searchTerm || filterStatus !== 'all'
                    ? 'No clients found matching your filters.'
                    : 'No clients assigned to you yet.'}
                </p>
              </div>
            ) : (
              paginatedClients.map((client, index) => {
                const stats = getClientStats(client.id);
                const clientName = getClientName(client);
                const initials = getInitials(client);
                return (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`cursor-pointer rounded-2xl border bg-white/80 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                      selectedClient?.id === client.id
                        ? 'border-blue-500 shadow-blue-100'
                        : 'border-black/10 hover:border-blue-200'
                    }`}
                    onClick={() => setSelectedClient(client)}
                  >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex flex-1 items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-lg font-semibold text-blue-700 shadow-inner">
                          {initials}
                        </div>
                        <div className="flex-1">
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <h4 className="text-lg font-semibold text-black">{clientName}</h4>
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-medium ${statusStyles(client.status)}`}
                            >
                              {client.status || 'active'}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-black/70">
                            {client.organization || client.businessName || 'No organization listed'}
                          </p>
                          <p className="text-xs text-black/50">{client.email}</p>
                        </div>
                      </div>
                      <div className="rounded-xl border border-black/10 px-4 py-3 text-right">
                        <p className="text-xs uppercase tracking-wide text-black/40">
                          Business Stage
                        </p>
                        <p className="text-sm font-semibold text-black">
                          {client.businessStage || 'N/A'}
                        </p>
                        <p className="text-xs text-black/50">{client.stage || 'Stage N/A'}</p>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-black/70 sm:grid-cols-4">
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-black/50">
                          Location
                        </p>
                        <p className="mt-1 font-medium">{client.location || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-black/50">Phone</p>
                        <p className="mt-1 font-medium">{client.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-black/50">Cohort</p>
                        <p className="mt-1 font-medium">
                          {client.cohortGroup || client.corhotGroup || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-black/50">
                          Timezone
                        </p>
                        <p className="mt-1 font-medium">{client.timezone || 'UTC'}</p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="mb-1 flex justify-between text-sm text-black/70">
                        <span>Engagement Progress</span>
                        <span>{stats.progress}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-blue-600 transition-all"
                          style={{ width: `${stats.progress}%` }}
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-black/50">
                        <span>{stats.totalSessions} sessions logged</span>
                        {stats.rating > 0 && (
                          <div className="flex items-center gap-1">
                            <StarIcon className="h-3 w-3 text-yellow-500" />
                            <span>{stats.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2 text-xs font-medium text-black/70">
                      {client.assigned && (
                        <span className="rounded-full bg-black/5 px-3 py-1">
                          Moderator: {client.assigned}
                        </span>
                      )}
                      {client.assignedCoachName && (
                        <span className="rounded-full bg-black/5 px-3 py-1">
                          Coach: {client.assignedCoachName}
                        </span>
                      )}
                      {client.businessStage && (
                        <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-600">
                          {client.businessStage}
                        </span>
                      )}
                      {client.stage && (
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-600">
                          {client.stage}
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col gap-4 border-t border-black/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-black/70">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredClients.length)} of{' '}
                {filteredClients.length} clients
              </div>
              <div className="flex items-center justify-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? 'cursor-not-allowed bg-gray-100 text-black/30'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Previous
                </motion.button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <motion.button
                        key={pageNum}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-black hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </motion.button>
                    );
                  })}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    currentPage === totalPages
                      ? 'cursor-not-allowed bg-gray-100 text-black/30'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4" />
                </motion.button>
              </div>
            </div>
          )}
        </div>

        {selectedClient &&
          (() => {
            const stats = getClientStats(selectedClient.id);
            return (
              <div>
                <h3 className="mb-4 text-lg font-medium text-black">Client Details</h3>
                <div className="rounded-2xl border border-black/10 p-5 shadow-sm">
                  <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-xl font-semibold text-blue-700 shadow-inner">
                        {getInitials(selectedClient)}
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-black">
                          {getClientName(selectedClient)}
                        </h4>
                        <p className="text-sm text-black/70">
                          {selectedClient.organization ||
                            selectedClient.businessName ||
                            'No organization'}
                        </p>
                        <p className="text-xs text-black/50">{selectedClient.email}</p>
                      </div>
                    </div>
                    <span
                      className={`self-start rounded-full px-3 py-1 text-xs font-medium ${statusStyles(selectedClient.status)}`}
                    >
                      {selectedClient.status || 'active'}
                    </span>
                  </div>

                  <div className="grid gap-4 text-sm text-black/70 sm:grid-cols-2">
                    <div className="rounded-xl bg-gray-50 p-4">
                      <p className="text-xs uppercase tracking-wide text-black/50">
                        Business Stage
                      </p>
                      <p className="mt-2 text-base font-semibold text-black">
                        {selectedClient.businessStage || 'N/A'}
                      </p>
                      <p className="text-xs text-black/50">{selectedClient.stage || 'Stage N/A'}</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-4">
                      <p className="text-xs uppercase tracking-wide text-black/50">Engagement</p>
                      <p className="mt-2 text-base font-semibold text-black">{stats.progress}%</p>
                      <p className="text-xs text-black/50">
                        {stats.totalSessions} sessions • Last on {stats.lastSession || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-4 text-sm text-black/80 sm:grid-cols-2">
                    {[
                      { label: 'Primary Contact', value: selectedClient.phone || 'Not provided' },
                      { label: 'Location', value: selectedClient.location || 'Unknown' },
                      {
                        label: 'Cohort',
                        value: selectedClient.cohortGroup || selectedClient.corhotGroup || '-',
                      },
                      { label: 'Timezone', value: selectedClient.timezone || 'UTC' },
                      {
                        label: 'Assigned Moderator',
                        value: selectedClient.assigned || 'Not assigned',
                      },
                      {
                        label: 'Assigned Coach',
                        value: selectedClient.assignedCoachName || 'Not assigned',
                      },
                    ].map(item => (
                      <div key={item.label} className="rounded-xl border border-black/5 p-3">
                        <p className="text-xs uppercase tracking-wide text-black/50">
                          {item.label}
                        </p>
                        <p className="mt-1 font-medium">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      Schedule Session
                    </motion.button>
                    <motion.a
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href={`mailto:${selectedClient.email}`}
                      className="flex items-center gap-2 rounded-lg border border-black/20 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
                    >
                      Contact Client
                    </motion.a>
                    {selectedClient.phone && (
                      <motion.a
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        href={`tel:${selectedClient.phone}`}
                        className="flex items-center gap-2 rounded-lg border border-black/20 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
                      >
                        Call
                      </motion.a>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
      </div>
    </motion.div>
  );
};

const SessionNotes = ({ filterClientId, filterDate, onNavigateToReports, onClearFilters }) => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [newNote, setNewNote] = useState({
    clientId: '',
    clientName: '',
    date: '',
    duration: '',
    type: 'strategy',
    notes: '',
    actionItems: [],
    rating: 0,
  });

  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Get effective email (parent coach email if delegated user)
    const delegatedUserData = localStorage.getItem('delegatedUser');
    let effectiveEmail = currentUser.email;

    if (delegatedUserData) {
      try {
        const parsed = JSON.parse(delegatedUserData);
        effectiveEmail = parsed.coachEmail || currentUser.email;
      } catch (e) {
        // Invalid JSON, use current user email
      }
    }

    // Fetch clients assigned to this coach
    const clientsQuery = query(
      collection(firestore, 'clients'),
      where('assignedCoachEmail', '==', effectiveEmail)
    );

    const unsubscribeClients = onSnapshot(
      clientsQuery,
      snapshot => {
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClients(clientsData);
      },
      () => {}
    );

    // Fetch session notes for this coach
    const notesQuery = query(
      collection(firestore, 'sessionNotes'),
      where('coachEmail', '==', effectiveEmail),
      orderBy('date', 'desc')
    );

    const unsubscribeNotes = onSnapshot(
      notesQuery,
      snapshot => {
        let sessionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Apply filters if provided
        if (filterClientId) {
          sessionsData = sessionsData.filter(session => session.clientId === filterClientId);
        }
        if (filterDate) {
          const filterDateStr =
            typeof filterDate === 'string'
              ? filterDate.split('T')[0]
              : filterDate.toISOString().split('T')[0];
          sessionsData = sessionsData.filter(session => {
            const sessionDateStr = session.date
              ? typeof session.date === 'string'
                ? session.date.split('T')[0]
                : new Date(session.date).toISOString().split('T')[0]
              : '';
            return sessionDateStr === filterDateStr;
          });
        }

        setSessions(sessionsData);
        setLoading(false);
      },
      error => {
        // If orderBy fails, try without it
        const simpleQuery = query(
          collection(firestore, 'sessionNotes'),
          where('coachEmail', '==', effectiveEmail)
        );
        onSnapshot(
          simpleQuery,
          snapshot => {
            const sessionsData = snapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
              }))
              .sort((a, b) => {
                const dateA = a.date ? new Date(a.date).getTime() : 0;
                const dateB = b.date ? new Date(b.date).getTime() : 0;
                return dateB - dateA;
              });
            // Apply filters if provided
            if (filterClientId) {
              sessionsData = sessionsData.filter(session => session.clientId === filterClientId);
            }
            if (filterDate) {
              const filterDateStr =
                typeof filterDate === 'string'
                  ? filterDate.split('T')[0]
                  : filterDate.toISOString().split('T')[0];
              sessionsData = sessionsData.filter(session => {
                const sessionDateStr = session.date
                  ? typeof session.date === 'string'
                    ? session.date.split('T')[0]
                    : new Date(session.date).toISOString().split('T')[0]
                  : '';
                return sessionDateStr === filterDateStr;
              });
            }

            setSessions(sessionsData);
            setLoading(false);
          },
          () => {
            toast.error('Failed to fetch session notes', 'Error');
            setLoading(false);
          }
        );
      }
    );

    return () => {
      unsubscribeClients();
      unsubscribeNotes();
    };
  }, [currentUser?.email, filterClientId, filterDate]);

  const addSessionNote = async () => {
    if (!newNote.clientId || !newNote.notes || !newNote.date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const noteData = {
        ...newNote,
        coachEmail: currentUser.email,
        coachName: currentUser.displayName || currentUser.email,
        status: 'completed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (editingSession) {
        await updateDoc(doc(firestore, 'sessionNotes', editingSession.id), {
          ...noteData,
          updatedAt: serverTimestamp(),
        });
        toast.success('Session note updated successfully!');
      } else {
        await addDoc(collection(firestore, 'sessionNotes'), noteData);
        toast.success('Session note added successfully!');
      }

      setNewNote({
        clientId: '',
        clientName: '',
        date: '',
        duration: '',
        type: 'strategy',
        notes: '',
        actionItems: [],
        rating: 0,
      });
      setShowNoteModal(false);
      setEditingSession(null);
    } catch (error) {
      toast.error('Failed to save session note', 'Error');
    }
  };

  const handleDeleteClick = session => {
    setSessionToDelete(session);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sessionToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'sessionNotes', sessionToDelete.id));
      toast.success('Session note deleted successfully!');
      setShowDeleteModal(false);
      setSessionToDelete(null);
    } catch (error) {
      toast.error('Failed to delete session note', 'Error');
    }
  };

  const handleEditSession = session => {
    setEditingSession(session);
    setNewNote({
      clientId: session.clientId || '',
      clientName: session.clientName || '',
      date: session.date || '',
      duration: session.duration || '',
      type: session.type || 'strategy',
      notes: session.notes || '',
      actionItems: session.actionItems || [],
      rating: session.rating || 0,
    });
    setShowNoteModal(true);
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading session notes...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-6 shadow-lg"
    >
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <DocumentTextIcon className="h-6 w-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-black">Session Notes</h2>
            {(filterClientId || filterDate) && (
              <p className="mt-1 text-xs text-blue-600">
                Filtered: {filterClientId ? 'Client' : ''}{' '}
                {filterDate ? `Date: ${new Date(filterDate).toLocaleDateString()}` : ''}
                {onClearFilters && (
                  <button
                    onClick={onClearFilters}
                    className="ml-2 text-blue-800 underline hover:text-blue-900"
                  >
                    Clear filters
                  </button>
                )}
              </p>
            )}
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowNoteModal(true)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Add Session Note
        </motion.button>
      </div>

      <div className="space-y-4">
        {sessions.length === 0 ? (
          <div className="py-8 text-center">
            <DocumentTextIcon className="mx-auto mb-4 h-12 w-12 text-blue-400" />
            <p className="text-blue-600">No session notes yet. Add your first session note!</p>
          </div>
        ) : (
          sessions.map((session, index) => {
            const sessionDate = session.date ? new Date(session.date).toLocaleDateString() : 'N/A';
            return (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="rounded-lg border border-black/20 p-4 transition-colors hover:border-black/30"
              >
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-black">
                      {session.clientName || 'Unknown Client'}
                    </h4>
                    <p className="text-sm text-black/70">
                      {sessionDate} • {session.duration || 'N/A'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-600">
                      {session.type || 'strategy'}
                    </span>
                    {session.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <StarIcon className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm">{session.rating}</span>
                      </div>
                    )}
                  </div>
                </div>

                <p className="mb-3 text-sm text-black/70">{session.notes}</p>

                {session.actionItems && session.actionItems.length > 0 && (
                  <div className="mb-3">
                    <h5 className="mb-2 text-sm font-medium text-black/70">Action Items</h5>
                    <ul className="space-y-1">
                      {session.actionItems.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-black/70">
                          <div className="h-1.5 w-1.5 rounded-full bg-black/20"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedSession(session)}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <EyeIcon className="h-4 w-4" />
                    View Details
                  </motion.button>
                  {onNavigateToReports && session.clientId && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onNavigateToReports(session.clientId)}
                      className="flex items-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                      title="View Reports for this Client"
                    >
                      <ClipboardDocumentListIcon className="h-4 w-4" />
                      View Report
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEditSession(session)}
                    className="flex items-center gap-2 rounded-lg border border-black/20 px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteClick(session)}
                    className="flex items-center gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </motion.button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      <AnimatePresence>
        {selectedSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setSelectedSession(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-black">
                    {selectedSession.clientName || 'Client Session'}
                  </h3>
                  <p className="text-sm text-black/60">
                    {selectedSession.date
                      ? new Date(selectedSession.date).toLocaleDateString()
                      : 'N/A'}{' '}
                    • {selectedSession.duration || 'N/A'} •{' '}
                    {selectedSession.type?.replace(/\b\w/g, l => l.toUpperCase()) || 'Strategy'}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-gray-100 p-4">
                  <p className="text-xs uppercase tracking-wide text-black/50">Rating</p>
                  <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-black">
                    <StarIcon className="h-5 w-5 text-yellow-500" />
                    {selectedSession.rating > 0 ? selectedSession.rating : 'Not rated'}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-100 p-4">
                  <p className="text-xs uppercase tracking-wide text-black/50">Status</p>
                  <span className="mt-2 inline-flex rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                    {selectedSession.status || 'completed'}
                  </span>
                </div>
              </div>

              <div className="mb-6 rounded-2xl bg-gray-50 p-4">
                <p className="text-xs uppercase tracking-wide text-black/50">Session Notes</p>
                <p className="mt-3 text-sm leading-relaxed text-black/80">
                  {selectedSession.notes || 'No notes provided.'}
                </p>
              </div>

              {selectedSession.actionItems && selectedSession.actionItems.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-black/50">Action Items</p>
                  <ul className="mt-3 space-y-2 text-sm text-black/80">
                    {selectedSession.actionItems.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-black/30" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showNoteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={() => {
              setShowNoteModal(false);
              setEditingSession(null);
              setNewNote({
                clientId: '',
                clientName: '',
                date: '',
                duration: '',
                type: 'strategy',
                notes: '',
                actionItems: [],
                rating: 0,
              });
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-4 shadow-2xl sm:p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 mb-4 flex items-center justify-between border-b bg-white pb-2">
                <h3 className="text-lg font-medium text-black">
                  {editingSession ? 'Edit Session Note' : 'Add Session Note'}
                </h3>
                <button
                  onClick={() => {
                    setShowNoteModal(false);
                    setEditingSession(null);
                    setNewNote({
                      clientId: '',
                      clientName: '',
                      date: '',
                      duration: '',
                      type: 'strategy',
                      notes: '',
                      actionItems: [],
                      rating: 0,
                    });
                  }}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black/70">Client *</label>
                    <select
                      value={newNote.clientId}
                      onChange={e => {
                        const selectedClient = clients.find(c => c.id === e.target.value);
                        setNewNote({
                          ...newNote,
                          clientId: e.target.value,
                          clientName: selectedClient
                            ? `${selectedClient.firstname || ''} ${selectedClient.lastname || ''}`.trim() ||
                              selectedClient.email
                            : '',
                        });
                      }}
                      className="w-full rounded-lg border border-black/20 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:p-3"
                    >
                      <option value="">Select a client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.firstname} {client.lastname} - {client.email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black/70">Date *</label>
                    <input
                      type="date"
                      value={newNote.date}
                      onChange={e => setNewNote({ ...newNote, date: e.target.value })}
                      className="w-full rounded-lg border border-black/20 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:p-3"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black/70">Duration</label>
                    <input
                      type="text"
                      value={newNote.duration}
                      onChange={e => setNewNote({ ...newNote, duration: e.target.value })}
                      placeholder="e.g., 1 hour"
                      className="w-full rounded-lg border border-black/20 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:p-3"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black/70">
                      Session Type
                    </label>
                    <select
                      value={newNote.type}
                      onChange={e => setNewNote({ ...newNote, type: e.target.value })}
                      className="w-full rounded-lg border border-black/20 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:p-3"
                    >
                      <option value="strategy">Strategy</option>
                      <option value="pitch practice">Pitch Practice</option>
                      <option value="financial">Financial Planning</option>
                      <option value="marketing">Marketing</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-black/70">Notes *</label>
                  <textarea
                    value={newNote.notes}
                    onChange={e => setNewNote({ ...newNote, notes: e.target.value })}
                    rows={4}
                    placeholder="Describe the session, key discussions, and outcomes..."
                    className="w-full resize-none rounded-lg border border-black/20 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:p-3"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-black/70">
                    Rating (1-5)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={newNote.rating}
                    onChange={e =>
                      setNewNote({ ...newNote, rating: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full rounded-lg border border-black/20 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:p-3"
                    placeholder="0-5"
                  />
                </div>
                <div className="sticky bottom-0 flex flex-col gap-2 border-t bg-white pb-2 pt-2 sm:flex-row">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={addSessionNote}
                    className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700 sm:text-base"
                  >
                    {editingSession ? 'Update Note' : 'Add Note'}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowNoteModal(false);
                      setEditingSession(null);
                      setNewNote({
                        clientId: '',
                        clientName: '',
                        date: '',
                        duration: '',
                        type: 'strategy',
                        notes: '',
                        actionItems: [],
                        rating: 0,
                      });
                    }}
                    className="flex-1 rounded-lg border border-black/20 px-4 py-2 text-sm text-black transition-colors hover:bg-black/5 sm:text-base"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSessionToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Session Note"
        message="Are you sure you want to delete this session note? This action cannot be undone."
        itemName={sessionToDelete?.clientName}
      />
    </motion.div>
  );
};

const CoachingCalendar = ({ onSessionClick }) => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Fetch session notes which can serve as calendar sessions
    const notesQuery = query(
      collection(firestore, 'sessionNotes'),
      where('coachEmail', '==', currentUser.email)
    );

    const unsubscribe = onSnapshot(
      notesQuery,
      snapshot => {
        const sessionsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            client: data.clientName || 'Unknown Client',
            date: data.date || '',
            time: data.time || 'TBD',
            duration: data.duration || 'N/A',
            type: data.type || 'strategy',
            status: data.status || 'completed',
            ...data,
          };
        });
        setSessions(sessionsData);
        setLoading(false);
      },
      error => {
        toast.error('Failed to fetch sessions', 'Error');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.email]);

  const getSessionsForDate = date => {
    const dateStr = date.toISOString().split('T')[0];
    return sessions.filter(session => session.date === dateStr);
  };

  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading calendar...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-6 shadow-lg"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-black">Coaching Calendar</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() =>
              setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))
            }
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            ←
          </button>
          <button
            onClick={() =>
              setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))
            }
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            →
          </button>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-lg font-medium text-black/70">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
      </div>

      <div className="mb-4 grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-black/50">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          if (!day) {
            return <div key={index} className="h-20"></div>;
          }

          const daySessions = getSessionsForDate(day);
          const isToday = day.toDateString() === new Date().toDateString();
          const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();

          return (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className={`h-20 cursor-pointer rounded-lg border p-1 transition-all ${
                isToday
                  ? 'border-blue-300 bg-blue-50'
                  : isSelected
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-black/20 hover:border-black/30'
              }`}
              onClick={() => setSelectedDate(day)}
            >
              <div className="mb-1 text-sm font-medium text-black/70">{day.getDate()}</div>
              <div className="space-y-1">
                {daySessions.slice(0, 2).map(session => (
                  <div
                    key={session.id}
                    className="rounded bg-blue-50 p-1 text-xs text-blue-600"
                    title={`${session.client} - ${session.time}`}
                  >
                    {session.client.length > 8
                      ? session.client.substring(0, 8) + '...'
                      : session.client}
                  </div>
                ))}
                {daySessions.length > 2 && (
                  <div className="text-xs text-black/50">+{daySessions.length - 2} more</div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 rounded-lg bg-gray-50 p-4"
        >
          <h4 className="mb-2 font-medium text-black">
            Sessions for {selectedDate.toLocaleDateString()}
          </h4>
          {getSessionsForDate(selectedDate).length > 0 ? (
            <div className="space-y-2">
              {getSessionsForDate(selectedDate).map(session => (
                <div
                  key={session.id}
                  className="flex items-center justify-between rounded bg-white p-2 transition-colors hover:bg-blue-50"
                >
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => onSessionClick && onSessionClick(session)}
                  >
                    <p className="text-sm font-medium text-black">{session.client}</p>
                    <p className="text-xs text-black/70">
                      {session.time} • {session.duration}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-600">
                      {session.type}
                    </span>
                    {onSessionClick && (
                      <button
                        onClick={() => onSessionClick(session)}
                        className="rounded-lg bg-blue-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                        title="View Session Notes"
                      >
                        View Notes
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-black/50">No sessions scheduled for this date</p>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

const PerformanceAssessment = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [sessionNotes, setSessionNotes] = useState([]);
  const [coachingLog, setCoachingLog] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedLogEntry, setSelectedLogEntry] = useState(null);
  const [editingLogEntry, setEditingLogEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [logEntryToDelete, setLogEntryToDelete] = useState(null);
  const [newLogEntry, setNewLogEntry] = useState({
    date: '',
    activity: '',
    client: '',
    hours: '',
    outcome: 'Positive',
    notes: '',
  });

  const resetLogForm = () => {
    setNewLogEntry({
      date: '',
      activity: '',
      client: '',
      hours: '',
      outcome: 'Positive',
      notes: '',
    });
    setEditingLogEntry(null);
  };

  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Get effective email (parent coach email if delegated user)
    const delegatedUserData = localStorage.getItem('delegatedUser');
    let effectiveEmail = currentUser.email;

    if (delegatedUserData) {
      try {
        const parsed = JSON.parse(delegatedUserData);
        effectiveEmail = parsed.coachEmail || currentUser.email;
      } catch (e) {
        // Invalid JSON, use current user email
      }
    }

    // Fetch clients assigned to this coach
    const clientsQuery = query(
      collection(firestore, 'clients'),
      where('assignedCoachEmail', '==', effectiveEmail)
    );

    const unsubscribeClients = onSnapshot(
      clientsQuery,
      snapshot => {
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClients(clientsData);
      },
      () => {}
    );

    // Fetch session notes
    const notesQuery = query(
      collection(firestore, 'sessionNotes'),
      where('coachEmail', '==', effectiveEmail)
    );

    const unsubscribeNotes = onSnapshot(
      notesQuery,
      snapshot => {
        const notesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSessionNotes(notesData);
        setLoading(false);
      },
      error => {
        toast.error('Failed to fetch session notes', 'Error');
        setLoading(false);
      }
    );

    // Fetch coaching log
    const logQuery = query(
      collection(firestore, 'coachingLog'),
      where('coachEmail', '==', effectiveEmail),
      orderBy('date', 'desc')
    );

    const unsubscribeLog = onSnapshot(
      logQuery,
      snapshot => {
        const logData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCoachingLog(logData);
      },
      error => {
        // If orderBy fails, try without it
        const simpleQuery = query(
          collection(firestore, 'coachingLog'),
          where('coachEmail', '==', effectiveEmail)
        );
        onSnapshot(
          simpleQuery,
          snapshot => {
            const logData = snapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
              }))
              .sort((a, b) => {
                const dateA = a.date ? new Date(a.date).getTime() : 0;
                const dateB = b.date ? new Date(b.date).getTime() : 0;
                return dateB - dateA;
              });
            setCoachingLog(logData);
          },
          () => {}
        );
      }
    );

    return () => {
      unsubscribeClients();
      unsubscribeNotes();
      unsubscribeLog();
    };
  }, [currentUser?.email]);

  // Calculate performance metrics
  const calculatePerformance = () => {
    const totalSessions = sessionNotes.length;
    const ratings = sessionNotes.filter(n => n.rating && n.rating > 0).map(n => n.rating);
    const averageRating =
      ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : 0;

    // Calculate monthly sessions
    const now = new Date();
    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const monthlySessions = sessionNotes.filter(session => {
      if (!session.date) return false;
      const sessionDate = new Date(session.date);
      return sessionDate >= oneMonthAgo;
    }).length;

    const previousMonthSessions = sessionNotes.filter(session => {
      if (!session.date) return false;
      const sessionDate = new Date(session.date);
      const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
      return sessionDate >= twoMonthsAgo && sessionDate < oneMonthAgo;
    }).length;

    const monthlyGrowth =
      previousMonthSessions > 0
        ? Math.round(((monthlySessions - previousMonthSessions) / previousMonthSessions) * 100)
        : monthlySessions > 0
          ? 100
          : 0;

    const completedSessions = sessionNotes.filter(s => s.status === 'completed').length;
    const sessionCompletion =
      totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;

    // Calculate total hours from coaching log and session notes
    const logHours = coachingLog.reduce((sum, entry) => {
      return sum + (parseFloat(entry.hours) || 0);
    }, 0);

    const notesHours = sessionNotes.reduce((sum, note) => {
      const duration = note.duration || '';
      const match = duration.match(/([\d.]+)/);
      return sum + (match ? parseFloat(match[1]) : 0);
    }, 0);

    const monthlyHours = Math.round(logHours + notesHours);

    // Client satisfaction based on ratings (simplified)
    const clientSatisfaction = ratings.length > 0 ? Math.round((averageRating / 5) * 100) : 0;

    return {
      totalSessions,
      averageRating: parseFloat(averageRating),
      clientSatisfaction,
      sessionCompletion,
      monthlyGrowth,
      monthlyHours,
    };
  };

  const performance = calculatePerformance();

  const addLogEntry = async () => {
    if (!newLogEntry.date || !newLogEntry.activity || !newLogEntry.client) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingLogEntry) {
        await updateDoc(doc(firestore, 'coachingLog', editingLogEntry.id), {
          ...newLogEntry,
          coachEmail: currentUser.email,
          coachName: currentUser.displayName || currentUser.email,
          updatedAt: serverTimestamp(),
        });
        toast.success('Log entry updated successfully!');
      } else {
        await addDoc(collection(firestore, 'coachingLog'), {
          ...newLogEntry,
          coachEmail: currentUser.email,
          coachName: currentUser.displayName || currentUser.email,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        toast.success('Log entry added successfully!');
      }

      resetLogForm();
      setShowLogModal(false);
    } catch (error) {
      toast.error('Failed to save log entry', 'Error');
    }
  };

  const handleEditLogEntry = entry => {
    setEditingLogEntry(entry);
    setNewLogEntry({
      date: entry.date || '',
      activity: entry.activity || '',
      client: entry.client || '',
      hours: entry.hours || '',
      outcome: entry.outcome || 'Positive',
      notes: entry.notes || '',
    });
    setShowLogModal(true);
  };

  const handleDeleteClick = entry => {
    setLogEntryToDelete(entry);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!logEntryToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'coachingLog', logEntryToDelete.id));
      toast.success('Log entry deleted successfully!');
      setShowDeleteModal(false);
      setLogEntryToDelete(null);
    } catch (error) {
      toast.error('Failed to delete log entry', 'Error');
    }
  };

  const handleViewLogEntry = entry => {
    setSelectedLogEntry(entry);
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading performance data...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-6 shadow-lg"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ChartBarIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-black">Performance Assessment</h2>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            resetLogForm();
            setShowLogModal(true);
          }}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Add Log Entry
        </motion.button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-5">
        <div className="rounded-lg bg-blue-50 p-4">
          <div className="flex items-center gap-2">
            <AcademicCapIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-black/70">Total Sessions</span>
          </div>
          <p className="text-2xl font-bold text-black">{performance.totalSessions}</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4">
          <div className="flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-black/70">Avg Rating</span>
          </div>
          <p className="text-2xl font-bold text-black">{performance.averageRating}</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4">
          <div className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-black/70">Satisfaction</span>
          </div>
          <p className="text-2xl font-bold text-black">{performance.clientSatisfaction}%</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-black/70">Completion</span>
          </div>
          <p className="text-2xl font-bold text-black">{performance.sessionCompletion}%</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4">
          <div className="flex items-center gap-2">
            <ArrowUpIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-black/70">Growth</span>
          </div>
          <p className="text-2xl font-bold text-black">+{performance.monthlyGrowth}%</p>
        </div>
      </div>

      <div>
        <h3 className="mb-4 text-lg font-medium text-black/70">Coaching Log</h3>
        <div className="space-y-3">
          {coachingLog.length === 0 ? (
            <div className="py-8 text-center">
              <ChartBarIcon className="mx-auto mb-4 h-12 w-12 text-blue-400" />
              <p className="text-blue-600">No coaching log entries yet. Add your first entry!</p>
            </div>
          ) : (
            coachingLog.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="rounded-lg border border-black/20 p-4"
              >
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-black">{entry.activity}</h4>
                    <p className="text-sm text-black/70">
                      {entry.client} •{' '}
                      {entry.date ? new Date(entry.date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-black/70">{entry.hours}h</span>
                    <span
                      className={`rounded-full px-2 py-1 text-xs ${
                        entry.outcome === 'Excellent'
                          ? 'bg-blue-50 text-blue-600'
                          : entry.outcome === 'Positive'
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-blue-50 text-blue-600'
                      }`}
                    >
                      {entry.outcome}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-black/70">{entry.notes}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleViewLogEntry(entry)}
                    className="flex items-center gap-2 rounded-lg border border-black/10 px-3 py-2 text-xs font-medium text-black transition-colors hover:border-blue-200 hover:bg-blue-50"
                  >
                    <EyeIcon className="h-4 w-4" />
                    View
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEditLogEntry(entry)}
                    className="flex items-center gap-2 rounded-lg border border-black/10 px-3 py-2 text-xs font-medium text-black transition-colors hover:border-blue-200 hover:bg-blue-50"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteClick(entry)}
                    className="flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 transition-colors hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </motion.button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      <AnimatePresence>
        {selectedLogEntry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setSelectedLogEntry(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-black">{selectedLogEntry.activity}</h3>
                  <p className="text-sm text-black/60">
                    {selectedLogEntry.client} •{' '}
                    {selectedLogEntry.date
                      ? new Date(selectedLogEntry.date).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedLogEntry(null)}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4 grid grid-cols-2 gap-4 text-sm text-black/70">
                <div>
                  <p className="text-xs uppercase tracking-wide text-black/50">Outcome</p>
                  <p className="mt-1 font-medium">{selectedLogEntry.outcome}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-black/50">Hours Logged</p>
                  <p className="mt-1 font-medium">{selectedLogEntry.hours || 'N/A'}h</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-black/50">Client</p>
                  <p className="mt-1 font-medium">{selectedLogEntry.client || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-black/50">Last Updated</p>
                  <p className="mt-1 font-medium">
                    {selectedLogEntry.updatedAt?.toDate
                      ? selectedLogEntry.updatedAt.toDate().toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {selectedLogEntry.notes && (
                <div className="rounded-xl bg-gray-50 p-4 text-sm text-black/70">
                  <p className="text-xs uppercase tracking-wide text-black/50">Notes</p>
                  <p className="mt-2 leading-relaxed">{selectedLogEntry.notes}</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLogModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md rounded-lg bg-white p-6"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-black">
                  {editingLogEntry ? 'Edit Log Entry' : 'Add Log Entry'}
                </h3>
                <button
                  onClick={() => {
                    setShowLogModal(false);
                    resetLogForm();
                  }}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black/70">Date</label>
                    <input
                      type="date"
                      value={newLogEntry.date}
                      onChange={e => setNewLogEntry({ ...newLogEntry, date: e.target.value })}
                      className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black/70">Hours</label>
                    <input
                      type="number"
                      step="0.5"
                      value={newLogEntry.hours}
                      onChange={e => setNewLogEntry({ ...newLogEntry, hours: e.target.value })}
                      className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-black/70">Activity</label>
                  <input
                    type="text"
                    value={newLogEntry.activity}
                    onChange={e => setNewLogEntry({ ...newLogEntry, activity: e.target.value })}
                    className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-black/70">Client</label>
                  <select
                    value={newLogEntry.client}
                    onChange={e => setNewLogEntry({ ...newLogEntry, client: e.target.value })}
                    className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select a client</option>
                    {clients.map(client => {
                      const clientName =
                        `${client.firstname || ''} ${client.lastname || ''}`.trim() ||
                        client.email ||
                        'Unknown';
                      return (
                        <option key={client.id} value={clientName}>
                          {clientName} - {client.email}
                        </option>
                      );
                    })}
                  </select>
                  {clients.length === 0 && (
                    <p className="mt-1 text-xs text-black/50">No clients available</p>
                  )}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-black/70">Outcome</label>
                  <select
                    value={newLogEntry.outcome}
                    onChange={e => setNewLogEntry({ ...newLogEntry, outcome: e.target.value })}
                    className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="Positive">Positive</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Needs Improvement">Needs Improvement</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-black/70">Notes</label>
                  <textarea
                    value={newLogEntry.notes}
                    onChange={e => setNewLogEntry({ ...newLogEntry, notes: e.target.value })}
                    rows={3}
                    className="w-full rounded-lg border border-black/20 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={addLogEntry}
                    className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                  >
                    {editingLogEntry ? 'Update Entry' : 'Add Entry'}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowLogModal(false);
                      resetLogForm();
                    }}
                    className="flex-1 rounded-lg border border-black/20 px-4 py-2 text-black transition-colors hover:bg-black/5"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setLogEntryToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Log Entry"
        message="Are you sure you want to delete this log entry? This action cannot be undone."
        itemName={logEntryToDelete?.activity}
      />
    </motion.div>
  );
};

const CoachTaskManager = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('dueDate');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [businessAdvisors, setBusinessAdvisors] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    assignedToEmail: '',
    assignedToName: '',
    assignedToType: 'client', // client, coach, businessadvisor
    category: 'general',
    estimatedHours: '',
    notes: '',
  });

  // Fetch tasks assigned to this coach OR created by this coach
  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);

    let unsubscribeCreated = null;
    let unsubscribeAssigned = null;

    // Helper function to combine and set tasks
    const combineAndSetTasks = (assignedTasks, createdTasks = []) => {
      const allTasks = [...assignedTasks, ...createdTasks];
      const uniqueTasks = allTasks.filter(
        (task, index, self) => index === self.findIndex(t => t.id === task.id)
      );
      setTasks(uniqueTasks);
      setLoading(false);
    };

    // Try with orderBy first
    const assignedTasksQuery = query(
      collection(firestore, 'tasks'),
      where('assignedToEmail', '==', currentUser.email),
      orderBy('createdAt', 'desc')
    );

    const createdTasksQuery = query(
      collection(firestore, 'tasks'),
      where('createdBy', '==', currentUser.email),
      orderBy('createdAt', 'desc')
    );

    unsubscribeAssigned = onSnapshot(
      assignedTasksQuery,
      snapshot => {
        const assignedTasks = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          taskType: 'assigned',
        }));

        // Fetch created tasks
        unsubscribeCreated = onSnapshot(
          createdTasksQuery,
          snapshot2 => {
            const createdTasks = snapshot2.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              taskType: 'created',
            }));
            combineAndSetTasks(assignedTasks, createdTasks);
          },
          error => {
            // If orderBy fails, try without it
            if (error.code === 'failed-precondition' || error.code === 'unavailable') {
              const simpleQuery = query(
                collection(firestore, 'tasks'),
                where('createdBy', '==', currentUser.email)
              );
              unsubscribeCreated = onSnapshot(
                simpleQuery,
                snapshot2 => {
                  const createdTasks = snapshot2.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    taskType: 'created',
                  }));
                  combineAndSetTasks(assignedTasks, createdTasks);
                },
                () => {
                  // If that fails, just use assigned tasks (no error, just no created tasks)
                  combineAndSetTasks(assignedTasks, []);
                }
              );
            } else {
              // For other errors, just use assigned tasks silently
              combineAndSetTasks(assignedTasks, []);
            }
          }
        );
      },
      error => {
        // If orderBy fails for assigned tasks, try without it
        if (error.code === 'failed-precondition' || error.code === 'unavailable') {
          const simpleQuery = query(
            collection(firestore, 'tasks'),
            where('assignedToEmail', '==', currentUser.email)
          );
          unsubscribeAssigned = onSnapshot(
            simpleQuery,
            snapshot => {
              const assignedTasks = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                taskType: 'assigned',
              }));

              // Try to fetch created tasks without orderBy
              const createdSimpleQuery = query(
                collection(firestore, 'tasks'),
                where('createdBy', '==', currentUser.email)
              );
              unsubscribeCreated = onSnapshot(
                createdSimpleQuery,
                snapshot2 => {
                  const createdTasks = snapshot2.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    taskType: 'created',
                  }));
                  combineAndSetTasks(assignedTasks, createdTasks);
                },
                () => {
                  // If that fails, just use assigned tasks (no error)
                  combineAndSetTasks(assignedTasks, []);
                }
              );
            },
            () => {
              // If everything fails, just set empty array (no tasks, no error)
              setTasks([]);
              setLoading(false);
            }
          );
        } else {
          // For other errors, just set empty array (no tasks, no error)
          setTasks([]);
          setLoading(false);
        }
      }
    );

    return () => {
      if (unsubscribeAssigned) unsubscribeAssigned();
      if (unsubscribeCreated) unsubscribeCreated();
    };
  }, [currentUser?.email]);

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const taskRef = doc(firestore, 'tasks', taskId);
      await updateDoc(taskRef, {
        status: newStatus,
        completedAt: newStatus === 'completed' ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });

      toast.success(`Task ${newStatus === 'completed' ? 'completed' : 'updated'}!`);
    } catch (error) {
      toast.error('Failed to update task status', 'Error');
    }
  };

  const handleSingleDeleteClick = task => {
    setTaskToDelete(task);
    setShowSingleDeleteModal(true);
  };

  const handleSingleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'tasks', taskToDelete.id));
      toast.success('Task deleted successfully');
      setSelectedTasks(prev => {
        const next = new Set(prev);
        next.delete(taskToDelete.id);
        return next;
      });
      setShowSingleDeleteModal(false);
      setTaskToDelete(null);
    } catch (error) {
      toast.error('Failed to delete task', 'Error');
    }
  };

  // Fetch users for task assignment
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const [clientsSnap, coachesSnap, advisorsSnap] = await Promise.all([
          getDocs(collection(firestore, 'clients')),
          getDocs(collection(firestore, 'coaches')),
          getDocs(collection(firestore, 'moderator')),
        ]);

        setClients(
          clientsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            email: doc.data().email,
            name:
              `${doc.data().firstname || ''} ${doc.data().lastname || ''}`.trim() ||
              doc.data().email,
            organization: doc.data().organization || doc.data().businessName || 'No organization',
            type: 'client',
          }))
        );

        setCoaches(
          coachesSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            email: doc.data().email,
            name:
              `${doc.data().firstname || ''} ${doc.data().lastname || ''}`.trim() ||
              doc.data().email,
            organization: doc.data().company || doc.data().organization || 'No organization',
            type: 'coach',
          }))
        );

        setBusinessAdvisors(
          advisorsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            email: doc.data().email,
            name:
              `${doc.data().firstname || ''} ${doc.data().lastname || ''}`.trim() ||
              doc.data().email,
            organization: doc.data().company || doc.data().organization || 'No organization',
            type: 'businessadvisor',
          }))
        );
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };

    fetchUsers();
  }, []);

  const resetTaskForm = () => {
    setNewTask({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: '',
      assignedToEmail: '',
      assignedToName: '',
      assignedToType: 'client',
      category: 'general',
      estimatedHours: '',
      notes: '',
    });
  };

  const addTask = async () => {
    if (!newTask.title || !newTask.description || !newTask.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!newTask.assignedToEmail) {
      toast.error('Please select a user to assign the task to');
      return;
    }

    try {
      const taskData = {
        ...newTask,
        status: 'pending',
        createdBy: currentUser.email,
        createdByName: currentUser.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(firestore, 'tasks'), taskData);
      setShowAddTask(false);
      resetTaskForm();
      toast.success('Task created successfully');
    } catch (error) {
      toast.error('Failed to create task');
      console.error(error);
    }
  };

  const editTask = async () => {
    if (!selectedTask || !newTask.title || !newTask.description || !newTask.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const taskRef = doc(firestore, 'tasks', selectedTask.id);
      await updateDoc(taskRef, {
        ...newTask,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });

      resetTaskForm();
      setShowEditTask(false);
      setSelectedTask(null);
      toast.success('Task updated successfully');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleEditTask = task => {
    setSelectedTask(task);
    let dueDateValue = '';
    if (task.dueDate) {
      if (task.dueDate.toDate) {
        dueDateValue = task.dueDate.toDate().toISOString().split('T')[0];
      } else if (task.dueDate instanceof Date) {
        dueDateValue = task.dueDate.toISOString().split('T')[0];
      } else if (typeof task.dueDate === 'string') {
        dueDateValue = task.dueDate.split('T')[0];
      }
    }
    setNewTask({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      dueDate: dueDateValue,
      assignedToEmail: task.assignedToEmail || '',
      assignedToName: task.assignedToName || '',
      assignedToType: task.assignedToType || 'client',
      category: task.category || 'general',
      estimatedHours: task.estimatedHours || '',
      notes: task.notes || '',
    });
    setShowEditTask(true);
  };

  // Bulk operations
  const toggleTaskSelection = taskId => {
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const selectAllTasks = () => {
    if (selectedTasks.size === filteredTasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(filteredTasks.map(t => t.id)));
    }
  };

  const bulkUpdateStatus = async newStatus => {
    if (selectedTasks.size === 0) {
      toast.error('Please select at least one task');
      return;
    }

    try {
      const batch = writeBatch(firestore);
      selectedTasks.forEach(taskId => {
        const taskRef = doc(firestore, 'tasks', taskId);
        batch.update(taskRef, {
          status: newStatus,
          completedAt: newStatus === 'completed' ? serverTimestamp() : null,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
        });
      });

      await batch.commit();
      toast.success(`${selectedTasks.size} task(s) updated successfully`);
      setSelectedTasks(new Set());
    } catch (error) {
      toast.error('Failed to update tasks');
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedTasks.size === 0) {
      toast.warning('Please select at least one task', 'Validation Error');
      return;
    }
    setShowDeleteModal(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedTasks.size === 0) return;

    try {
      const batch = writeBatch(firestore);
      selectedTasks.forEach(taskId => {
        const taskRef = doc(firestore, 'tasks', taskId);
        batch.delete(taskRef);
      });

      await batch.commit();
      toast.success(`${selectedTasks.size} task(s) deleted successfully`);
      setSelectedTasks(new Set());
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Failed to delete tasks', 'Error');
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return 'bg-red-50 text-red-600 border-red-200';
      case 'medium':
        return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'low':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'in-progress':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'pending':
        return 'bg-gray-50 text-gray-600 border-gray-200';
      case 'overdue':
        return 'bg-red-50 text-red-600 border-red-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getCategoryIcon = category => {
    switch (category) {
      case 'planning':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'finance':
        return <ChartBarIcon className="h-4 w-4" />;
      case 'research':
        return <AcademicCapIcon className="h-4 w-4" />;
      case 'meeting':
        return <CalendarIcon className="h-4 w-4" />;
      default:
        return <DocumentTextIcon className="h-4 w-4" />;
    }
  };

  const filteredTasks = tasks
    .filter(task => {
      if (filter !== 'all' && task.status !== filter) return false;
      if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'dueDate':
          return new Date(a.dueDate) - new Date(b.dueDate);
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

  const taskStats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    overdue: tasks.filter(t => t.status === 'overdue').length,
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading tasks...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-6 shadow-lg"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-black">My Tasks</h2>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddTask(true)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Create Task
        </motion.button>
      </div>

      {/* Bulk Operations */}
      {selectedTasks.size > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 p-4"
        >
          <span className="text-sm font-medium text-blue-900">
            {selectedTasks.size} task(s) selected
          </span>
          <div className="flex gap-2">
            <select
              onChange={e => bulkUpdateStatus(e.target.value)}
              className="rounded-lg border border-blue-300 bg-white px-3 py-1 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Bulk Update Status</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="overdue">Overdue</option>
            </select>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBulkDeleteClick}
              className="rounded-lg bg-red-600 px-4 py-1 text-sm font-medium text-white hover:bg-red-700"
            >
              Delete Selected
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedTasks(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear Selection
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* Task Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-5">
        <div className="rounded-lg bg-gray-50 p-3 text-center">
          <div className="text-lg font-bold text-black">{taskStats.total}</div>
          <div className="text-xs text-gray-600">Total</div>
        </div>
        <div className="rounded-lg bg-emerald-50 p-3 text-center">
          <div className="text-lg font-bold text-emerald-600">{taskStats.completed}</div>
          <div className="text-xs text-emerald-600">Completed</div>
        </div>
        <div className="rounded-lg bg-blue-50 p-3 text-center">
          <div className="text-lg font-bold text-blue-600">{taskStats.inProgress}</div>
          <div className="text-xs text-blue-600">In Progress</div>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 text-center">
          <div className="text-lg font-bold text-gray-600">{taskStats.pending}</div>
          <div className="text-xs text-gray-600">Pending</div>
        </div>
        <div className="rounded-lg bg-red-50 p-3 text-center">
          <div className="text-lg font-bold text-red-600">{taskStats.overdue}</div>
          <div className="text-xs text-red-600">Overdue</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'in-progress', 'completed', 'overdue'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`rounded-lg px-3 py-1 text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-black hover:bg-gray-200'
              }`}
            >
              {status === 'all'
                ? 'All Tasks'
                : status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="dueDate">Sort by Due Date</option>
            <option value="priority">Sort by Priority</option>
            <option value="status">Sort by Status</option>
          </select>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {filteredTasks.length > 0 && (
          <div className="flex items-center gap-2 pb-2">
            <input
              type="checkbox"
              checked={selectedTasks.size === filteredTasks.length && filteredTasks.length > 0}
              onChange={selectAllTasks}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Select All</span>
          </div>
        )}
        {filteredTasks.map((task, index) => (
          <motion.div
            key={task.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`group rounded-lg border p-4 transition-all ${
              selectedTasks.has(task.id)
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'
            }`}
          >
            <div className="mb-2 flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedTasks.has(task.id)}
                onChange={() => toggleTaskSelection(task.id)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>
            <div className="mb-3 flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="mt-1 text-blue-600">{getCategoryIcon(task.category)}</div>
                <div className="flex-1">
                  <h3 className="font-medium text-black">{task.title}</h3>
                  <p className="mt-1 text-sm text-black/70">{task.description}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <span
                  className={`rounded-full border px-2 py-1 text-xs font-medium ${getPriorityColor(task.priority)}`}
                >
                  {task.priority}
                </span>
                <span
                  className={`rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor(task.status)}`}
                >
                  {task.status}
                </span>
              </div>
            </div>

            <div className="mb-3 flex flex-wrap gap-4 text-sm text-black/70">
              {task.clientName && (
                <div className="flex items-center gap-1">
                  <UserGroupIcon className="h-4 w-4" />
                  <span>Client:</span>
                  <span className="font-medium">{task.clientName}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <CalendarIcon className="h-4 w-4" />
                <span>Due:</span>
                <span
                  className={`font-medium ${
                    task.status === 'overdue'
                      ? 'text-red-600'
                      : new Date(task.dueDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                        ? 'text-amber-600'
                        : 'text-black'
                  }`}
                >
                  {new Date(task.dueDate).toLocaleDateString()}
                </span>
              </div>
              {task.completedAt && (
                <div className="flex items-center gap-1">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
                  <span>Completed:</span>
                  <span className="text-emerald-600">
                    {task.completedAt?.toDate
                      ? new Date(task.completedAt.toDate()).toLocaleDateString()
                      : typeof task.completedAt === 'object' && task.completedAt !== null
                        ? new Date(task.completedAt.seconds * 1000).toLocaleDateString()
                        : new Date(task.completedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {task.status === 'pending' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => updateTaskStatus(task.id, 'in-progress')}
                  className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Start Task
                </motion.button>
              )}
              {(task.status === 'in-progress' || task.status === 'overdue') && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => updateTaskStatus(task.id, 'completed')}
                  className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-emerald-700"
                >
                  Mark Complete
                </motion.button>
              )}
              {task.status === 'completed' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => updateTaskStatus(task.id, 'in-progress')}
                  className="rounded-lg border border-blue-600 px-3 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
                >
                  Reopen
                </motion.button>
              )}
              {(task.taskType === 'created' || task.createdBy === currentUser.email) && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEditTask(task)}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Edit
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSingleDeleteClick(task)}
                    className="rounded-lg border border-red-300 px-3 py-1 text-xs font-medium text-red-700 transition-colors hover:border-red-400 hover:bg-red-50"
                  >
                    Delete
                  </motion.button>
                </>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {filteredTasks.length === 0 && (
        <div className="py-8 text-center">
          <ClipboardDocumentListIcon className="mx-auto mb-4 h-12 w-12 text-blue-400" />
          <p className="text-blue-600">No tasks found matching your criteria.</p>
        </div>
      )}

      {/* Create Task Modal */}
      <AnimatePresence>
        {showAddTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={() => {
              setShowAddTask(false);
              resetTaskForm();
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">Create New Task</h3>
                  <p className="text-sm text-gray-600">Assign a task to any user</p>
                </div>
                <button
                  onClick={() => {
                    setShowAddTask(false);
                    resetTaskForm();
                  }}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Task Title *
                  </label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter task title"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="4"
                    placeholder="Task description"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Assign To *
                  </label>
                  <select
                    value={newTask.assignedToType}
                    onChange={e => {
                      setNewTask({
                        ...newTask,
                        assignedToType: e.target.value,
                        assignedToEmail: '',
                        assignedToName: '',
                      });
                    }}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="client">Client</option>
                    <option value="coach">Coach</option>
                    <option value="businessadvisor">Business Advisor</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Select User *
                  </label>
                  <select
                    value={newTask.assignedToEmail}
                    onChange={e => {
                      const email = e.target.value;
                      let userList = [];
                      if (newTask.assignedToType === 'client') userList = clients;
                      else if (newTask.assignedToType === 'coach') userList = coaches;
                      else userList = businessAdvisors;

                      const selectedUser = userList.find(u => u.email === email);
                      setNewTask({
                        ...newTask,
                        assignedToEmail: email,
                        assignedToName: selectedUser ? selectedUser.name : '',
                      });
                    }}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select a user</option>
                    {(newTask.assignedToType === 'client'
                      ? clients
                      : newTask.assignedToType === 'coach'
                        ? coaches
                        : businessAdvisors
                    ).map(user => (
                      <option key={user.email} value={user.email}>
                        {user.organization} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Priority</label>
                    <select
                      value={newTask.priority}
                      onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Category</label>
                    <select
                      value={newTask.category}
                      onChange={e => setNewTask({ ...newTask, category: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="general">General</option>
                      <option value="planning">Planning</option>
                      <option value="finance">Finance</option>
                      <option value="research">Research</option>
                      <option value="meeting">Meeting</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={newTask.estimatedHours}
                      onChange={e => setNewTask({ ...newTask, estimatedHours: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="2.5"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Due Date *</label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={e => setNewTask({ ...newTask, dueDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={newTask.notes}
                    onChange={e => setNewTask({ ...newTask, notes: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={addTask}
                    className="flex-1 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white shadow-lg transition-all hover:bg-blue-700"
                  >
                    Create Task
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowAddTask(false);
                      resetTaskForm();
                    }}
                    className="flex-1 rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-700 transition-all hover:bg-gray-50"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Task Modal */}
      <AnimatePresence>
        {showEditTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={() => {
              setShowEditTask(false);
              setSelectedTask(null);
              resetTaskForm();
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">Edit Task</h3>
                  <p className="text-sm text-gray-600">Update task details</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditTask(false);
                    setSelectedTask(null);
                    resetTaskForm();
                  }}
                  className="rounded-lg p-2 hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Task Title *
                  </label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="4"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Priority</label>
                    <select
                      value={newTask.priority}
                      onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Category</label>
                    <select
                      value={newTask.category}
                      onChange={e => setNewTask({ ...newTask, category: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="general">General</option>
                      <option value="planning">Planning</option>
                      <option value="finance">Finance</option>
                      <option value="research">Research</option>
                      <option value="meeting">Meeting</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={newTask.estimatedHours}
                      onChange={e => setNewTask({ ...newTask, estimatedHours: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Due Date *</label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={e => setNewTask({ ...newTask, dueDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={newTask.notes}
                    onChange={e => setNewTask({ ...newTask, notes: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="3"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={editTask}
                    className="flex-1 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white shadow-lg transition-all hover:bg-blue-700"
                  >
                    Update Task
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowEditTask(false);
                      setSelectedTask(null);
                      resetTaskForm();
                    }}
                    className="flex-1 rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-700 transition-all hover:bg-gray-50"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={handleBulkDeleteConfirm}
        title="Delete Tasks"
        message={`Are you sure you want to delete ${selectedTasks.size} task(s)? This action cannot be undone.`}
      />

      {/* Single Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showSingleDeleteModal}
        onClose={() => {
          setShowSingleDeleteModal(false);
          setTaskToDelete(null);
        }}
        onConfirm={handleSingleDeleteConfirm}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        itemName={taskToDelete?.title}
      />
    </motion.div>
  );
};

const CoachesCRM = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('clients');
  const [sessionNotesFilter, setSessionNotesFilter] = useState({
    clientId: null,
    date: null,
  });
  const [reportsClientId, setReportsClientId] = useState(null);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeSessions: 0,
    averageRating: 0,
    monthlyHours: 0,
  });
  // Client Management state
  const [clientsData, setClientsData] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientFilters, setClientFilters] = useState({ status: '', organization: '' });
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [editClientForm, setEditClientForm] = useState({});

  useEffect(() => {
    if (!currentUser?.email) {
      return;
    }

    // Get effective email (parent coach email if delegated user)
    const delegatedUserData = localStorage.getItem('delegatedUser');
    let effectiveEmail = currentUser.email;

    if (delegatedUserData) {
      try {
        const parsed = JSON.parse(delegatedUserData);
        effectiveEmail = parsed.coachEmail || currentUser.email;
      } catch (e) {
        // Invalid JSON, use current user email
      }
    }

    // Fetch clients assigned to this coach
    const clientsQuery = query(
      collection(firestore, 'clients'),
      where('assignedCoachEmail', '==', effectiveEmail)
    );

    const unsubscribeClients = onSnapshot(
      clientsQuery,
      snapshot => {
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        const totalClients = snapshot.size;
        setStats(prev => ({ ...prev, totalClients }));
        setClientsData(clientsData);
        setClientsLoading(false);
      },
      () => {
        setClientsLoading(false);
      }
    );

    // Fetch session notes to calculate stats
    const notesQuery = query(
      collection(firestore, 'sessionNotes'),
      where('coachEmail', '==', effectiveEmail)
    );

    const unsubscribeNotes = onSnapshot(
      notesQuery,
      snapshot => {
        const notesData = snapshot.docs.map(doc => doc.data());
        const activeSessions = notesData.filter(
          n => n.status === 'scheduled' || n.status === 'in-progress'
        ).length;

        const ratings = notesData.filter(n => n.rating && n.rating > 0).map(n => n.rating);
        const averageRating =
          ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : 0;

        // Calculate monthly hours
        const now = new Date();
        const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        const monthlyNotes = notesData.filter(note => {
          if (!note.date) return false;
          const noteDate = new Date(note.date);
          return noteDate >= oneMonthAgo;
        });

        const notesHours = monthlyNotes.reduce((sum, note) => {
          const duration = note.duration || '';
          const match = duration.match(/([\d.]+)/);
          return sum + (match ? parseFloat(match[1]) : 0);
        }, 0);

        // Also fetch coaching log for this month
        const logQuery = query(
          collection(firestore, 'coachingLog'),
          where('coachEmail', '==', currentUser.email)
        );

        onSnapshot(
          logQuery,
          logSnapshot => {
            const logData = logSnapshot.docs.map(doc => doc.data());
            const monthlyLogs = logData.filter(entry => {
              if (!entry.date) return false;
              const entryDate = new Date(entry.date);
              return entryDate >= oneMonthAgo;
            });

            const logHours = monthlyLogs.reduce((sum, entry) => {
              return sum + (parseFloat(entry.hours) || 0);
            }, 0);

            const monthlyHours = Math.round(notesHours + logHours);

            setStats(prev => ({
              ...prev,
              activeSessions,
              averageRating: parseFloat(averageRating),
              monthlyHours,
            }));
          },
          () => {}
        );

        setStats(prev => ({
          ...prev,
          activeSessions,
          averageRating: parseFloat(averageRating),
          monthlyHours: Math.round(notesHours),
        }));
      },
      () => {}
    );

    return () => {
      unsubscribeClients();
      unsubscribeNotes();
    };
  }, [currentUser?.email]);

  // Client Management handlers
  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]).filter(key => key !== 'id');
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers
          .map(header => {
            const value = row[header];
            return typeof value === 'string' && value.includes(',') ? `"${value}"` : value || '';
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`${filename} exported successfully`);
  };

  const handleViewClient = client => {
    setSelectedClient(client);
    setShowClientModal(true);
  };

  const handleEditClient = client => {
    setEditClientForm(client);
    setShowEditClient(true);
  };

  const handleDeleteClick = client => {
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'clients', clientToDelete.id));
      toast.success('Client deleted successfully');
      setShowDeleteModal(false);
      setClientToDelete(null);
    } catch (error) {
      toast.error('Failed to delete client', 'Error');
    }
  };

  const handleUpdateClient = async () => {
    try {
      await updateDoc(doc(firestore, 'clients', editClientForm.id), {
        ...editClientForm,
        updatedAt: serverTimestamp(),
      });
      toast.success('Client updated successfully');
      setShowEditClient(false);
      setEditClientForm({});
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  const clientsTableConfig = {
    columns: [
      { key: 'firstname', label: 'First Name', sortable: true },
      { key: 'lastname', label: 'Last Name', sortable: true },
      { key: 'organization', label: 'Organization', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: value => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-medium ${
              value === 'active'
                ? 'bg-emerald-50 text-emerald-600'
                : value === 'inactive'
                  ? 'bg-red-50 text-red-600'
                  : 'bg-gray-50 text-gray-600'
            }`}
          >
            {value || 'active'}
          </span>
        ),
      },
    ],
  };

  const tabs = [
    { id: 'clients', name: 'Assigned Clients', icon: UserGroupIcon },
    { id: 'client-management', name: 'Client Management', icon: BuildingOfficeIcon },
    { id: 'sessions', name: 'Session Notes', icon: DocumentTextIcon },
    { id: 'calendar', name: 'Calendar', icon: CalendarIcon },
    { id: 'performance', name: 'Performance', icon: ChartBarIcon },
    { id: 'tasks', name: 'Task Management', icon: ClipboardDocumentListIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-black">Coach Management Dashboard</h1>
          <p className="text-black/70">
            Manage your assigned clients, track sessions, and monitor performance
          </p>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl bg-white p-6 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-black">Total Clients</p>
                <p className="text-2xl font-bold text-black">{stats.totalClients}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl bg-white p-6 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <ClockIcon className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-black">Active Sessions</p>
                <p className="text-2xl font-bold text-black">{stats.activeSessions}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl bg-white p-6 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <StarIcon className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-black">Average Rating</p>
                <p className="text-2xl font-bold text-black">{stats.averageRating}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-xl bg-white p-6 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-black">Monthly Hours</p>
                <p className="text-2xl font-bold text-black">{stats.monthlyHours}h</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {tabs.map(tab => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'border border-black/20 bg-white text-black hover:bg-black/5 hover:text-black'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.name}
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'clients' && <AssignedClientsDashboard />}
            {activeTab === 'client-management' && (
              <>
                <LiveTable
                  title="Clients Management"
                  data={clientsData}
                  columns={clientsTableConfig.columns}
                  filters={{
                    status: clientFilters.status || '',
                    organization: clientFilters.organization || '',
                  }}
                  searchTerm={clientSearchTerm || ''}
                  onSearchChange={value => setClientSearchTerm(value)}
                  onFilterChange={(key, value) =>
                    setClientFilters(prev => ({ ...prev, [key]: value }))
                  }
                  onExport={data => exportToCSV(data, 'clients')}
                  onView={handleViewClient}
                  onEdit={handleEditClient}
                  onDelete={handleDeleteClick}
                  loading={clientsLoading}
                />
                <AnimatePresence>
                  {showClientModal && selectedClient && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
                      >
                        <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-sm">
                              {(selectedClient.firstname ||
                                selectedClient.lastname ||
                                selectedClient.id)?.[0]?.toUpperCase() || 'C'}
                            </div>
                            <div>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-base font-semibold text-black">
                                  {selectedClient.firstname || selectedClient.lastname || '-'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {selectedClient.email}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                            onClick={() => setShowClientModal(false)}
                            aria-label="Close"
                          >
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="space-y-8 p-6">
                          <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                            Client Details
                          </div>
                          <div className="grid grid-cols-1 gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="font-medium">First Name:</span>
                                <span className="font-semibold text-black">
                                  {selectedClient.firstname || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="font-medium">Last Name:</span>
                                <span className="font-semibold text-black">
                                  {selectedClient.lastname || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="font-medium">Email:</span>
                                <span className="font-semibold text-black">
                                  {selectedClient.email || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="font-medium">Phone:</span>
                                <span className="font-semibold text-black">
                                  {selectedClient.phone || '-'}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="font-medium">Organization:</span>
                                <span className="font-semibold text-black">
                                  {selectedClient.organization || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="font-medium">Status:</span>
                                <span className="font-semibold text-black">
                                  {selectedClient.status || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="font-medium">Business Stage:</span>
                                <span className="font-semibold text-black">
                                  {selectedClient.businessStage || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <span className="font-medium">Location:</span>
                                <span className="font-semibold text-black">
                                  {selectedClient.location || '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditClientForm(selectedClient);
                                setShowEditClient(true);
                                setShowClientModal(false);
                              }}
                              className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700"
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteClick(selectedClient)}
                              className="rounded-lg bg-red-100 px-4 py-2 text-red-800 shadow hover:bg-red-200"
                            >
                              Delete
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Edit Client Modal */}
                <AnimatePresence>
                  {showEditClient && editClientForm && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-white shadow-2xl"
                      >
                        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                          <h3 className="text-lg font-semibold text-black">Edit Client</h3>
                          <button
                            onClick={() => {
                              setShowEditClient(false);
                              setEditClientForm({});
                            }}
                            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          >
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="space-y-4 p-6">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="mb-2 block text-sm font-medium text-black">
                                First Name
                              </label>
                              <input
                                type="text"
                                value={editClientForm.firstname || ''}
                                onChange={e =>
                                  setEditClientForm({
                                    ...editClientForm,
                                    firstname: e.target.value,
                                  })
                                }
                                className="w-full rounded-lg border border-black/20 px-3 py-2 text-black focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                              />
                            </div>
                            <div>
                              <label className="mb-2 block text-sm font-medium text-black">
                                Last Name
                              </label>
                              <input
                                type="text"
                                value={editClientForm.lastname || ''}
                                onChange={e =>
                                  setEditClientForm({ ...editClientForm, lastname: e.target.value })
                                }
                                className="w-full rounded-lg border border-black/20 px-3 py-2 text-black focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-black">
                              Email
                            </label>
                            <input
                              type="email"
                              value={editClientForm.email || ''}
                              onChange={e =>
                                setEditClientForm({ ...editClientForm, email: e.target.value })
                              }
                              className="w-full rounded-lg border border-black/20 px-3 py-2 text-black focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-black">
                              Phone
                            </label>
                            <input
                              type="text"
                              value={editClientForm.phone || ''}
                              onChange={e =>
                                setEditClientForm({ ...editClientForm, phone: e.target.value })
                              }
                              className="w-full rounded-lg border border-black/20 px-3 py-2 text-black focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-black">
                              Organization
                            </label>
                            <input
                              type="text"
                              value={editClientForm.organization || ''}
                              onChange={e =>
                                setEditClientForm({
                                  ...editClientForm,
                                  organization: e.target.value,
                                })
                              }
                              className="w-full rounded-lg border border-black/20 px-3 py-2 text-black focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-black">
                              Status
                            </label>
                            <select
                              value={editClientForm.status || 'active'}
                              onChange={e =>
                                setEditClientForm({ ...editClientForm, status: e.target.value })
                              }
                              className="w-full rounded-lg border border-black/20 px-3 py-2 text-black focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                          <div className="flex justify-end gap-2 pt-4">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setShowEditClient(false);
                                setEditClientForm({});
                              }}
                              className="rounded-lg border border-black/20 px-4 py-2 text-black hover:bg-black/5"
                            >
                              Cancel
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={handleUpdateClient}
                              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                            >
                              Save Changes
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
            {activeTab === 'sessions' && (
              <SessionNotes
                filterClientId={sessionNotesFilter.clientId}
                filterDate={sessionNotesFilter.date}
                onNavigateToReports={clientId => {
                  setReportsClientId(clientId);
                  // Navigate to reports section in the coach dashboard
                  // Dispatch a custom event that the parent dashboard can listen to
                  window.dispatchEvent(
                    new CustomEvent('navigate-to-monthly-reports', {
                      detail: { clientId },
                    })
                  );
                  toast.success('Opening reports for this client...');
                }}
                onClearFilters={() => {
                  setSessionNotesFilter({ clientId: null, date: null });
                }}
              />
            )}
            {activeTab === 'calendar' && (
              <CoachingCalendar
                onSessionClick={session => {
                  // Navigate to sessions tab and filter by this session
                  setSessionNotesFilter({
                    clientId: session.clientId || null,
                    date: session.date || null,
                  });
                  setActiveTab('sessions');
                  toast.success(
                    `Showing session notes for ${session.client || 'selected session'}`
                  );
                }}
              />
            )}
            {activeTab === 'performance' && <PerformanceAssessment />}
            {activeTab === 'tasks' && <CoachTaskManager />}
          </motion.div>
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmation
          open={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setClientToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Delete Client"
          message="Are you sure you want to delete this client? This action cannot be undone."
          itemName={clientToDelete?.firstname || clientToDelete?.email}
        />
      </div>
    </div>
  );
};

export default CoachesCRM;
